import { logAppInfo, recordAppError } from "@shared/utils/errors/errorLogger";
import {
  RoktClientBlockedError,
  RoktLauncherInstance,
} from "./types/rokt-types";

let roktPromise: Promise<RoktLauncherInstance | null> | null = null;

const loadRoktScript = async (
  accountId: string,
  sandbox = true
): Promise<RoktLauncherInstance> => {
  logAppInfo(`Loading Rokt with account ID: ${accountId}`, {
    category: "RoktLoader",
    data: { accountId, sandbox },
  });

  // Load the script if not already present
  if (!document.getElementById("rokt-launcher")) {
    const script = document.createElement("script");
    script.id = "rokt-launcher";
    script.src = "https://apps.rokt.com/wsdk/integrations/launcher.js";
    script.async = true;

    const scriptPromise = new Promise<void>((resolve, reject) => {
      script.onload = () => {
        logAppInfo("Rokt script loaded successfully", {
          category: "RoktLoader",
        });
        resolve();
      };
      script.onerror = () => {
        // Client-side blocking (ad blockers, etc.) - silently handle
        reject(new RoktClientBlockedError());
      };
    });

    document.head.appendChild(script);
    await scriptPromise;
  }

  // Initialize Rokt launcher
  if (window.Rokt) {
    try {
      const launcher = await window.Rokt.createLauncher({
        accountId,
        sandbox,
      });

      logAppInfo("Rokt launcher created successfully", {
        category: "RoktLoader",
        data: { accountId },
      });

      return launcher;
    } catch (launcherError) {
      const error = new Error(
        `Rokt launcher creation failed: ${
          launcherError instanceof Error
            ? launcherError.message
            : JSON.stringify(launcherError)
        }`
      );

      logAppInfo("Rokt launcher creation failed", {
        category: "RoktLoader",
        level: "error",
        data: {
          accountId,
          sandbox,
          originalError: String(launcherError),
        },
      });

      recordAppError(error);
      throw error;
    }
  }

  const error = new Error("window.Rokt not available");
  logAppInfo("window.Rokt not available", {
    category: "RoktLoader",
    level: "error",
    data: {
      windowRoktExists: !!window.Rokt,
      accountId,
    },
  });

  recordAppError(error);
  throw error;
};

export const getRoktLauncher = (accountId: string, sandbox = true) => {
  if (roktPromise) {
    return roktPromise;
  }

  roktPromise = loadRoktScript(accountId, sandbox).catch((error) => {
    // Silently handle client-side blocking, log actual errors
    if (!(error instanceof RoktClientBlockedError)) {
      logAppInfo("Rokt promise rejected, resetting", {
        category: "RoktLoader",
        level: "error",
        data: { error: String(error) },
      });
    }

    roktPromise = null;
    return null;
  });

  return roktPromise;
};

export const loadRokt = async (accountId: string, sandbox = true) => {
  return getRoktLauncher(accountId, sandbox);
};
